import React, {useEffect, useState} from "react";
import {connect} from "react-redux";
import {BackButton, hasPermission} from "../../sc-utilities";
import {Button, Dialog, Fab, IconButton, Tooltip, Typography} from "@material-ui/core";
import {ChevronRight, ExpandMore, Refresh, TableChart} from "@material-ui/icons";
import {withStyles} from "@material-ui/core/styles";
import {TreeItem, TreeView} from "@material-ui/lab";
import AppLogoLoading from "../../sc-logo";
import * as groupActions from "./store/groupActions";

const styles = theme => ({
    root: {
        marginTop: "7px",
        padding: theme.spacing(),
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px'
    },
    customToolbar: {
        paddingLeft: theme.spacing(4),
    },
    folderStructureIcon: {
        marginRight: theme.spacing(),
    },
    treeView: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3),
        flexGrow: 1,
    },
    loading: {
        backgroundColor: "transparent",
        boxShadow: "none",
        overflow: "hidden",
        alignItems: "center"
    },
});

const GroupFolderStructureView = (props) => {
    const { classes, translations, setShowTreeView, fetchAncestorGroups, activeCustomerID, permissions } = props;

    const [groups, setGroups] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    const [expanded, setExpanded] = useState([]);
    const [selected, setSelected] = useState([]);

    const getGroups = async () => {
        setLoading(true);
        const result = await fetchAncestorGroups({ActiveCustomerID: activeCustomerID});
        if (result && result instanceof Array) {
            setGroups(result);
        }
        setLoading(false);
    }

    useEffect(() => {
        if (hasPermission(permissions, 'group_list')) {
            getGroups();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const header =
        <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography className={classes.typography}>{translations['group'] || 'group'}</Typography>
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" onClick={() => getGroups()}>
                        <Refresh />
                    </IconButton>
                </Tooltip>

                <Fab
                    id="view_as_table"
                    variant="extended"
                    size="small"
                    color="primary"
                    aria-label="View as table"
                    style={{ borderRadius: '1px' }}
                    className={classes.fab}
                    onClick={() => setShowTreeView(false)}
                >
                    <TableChart size="small" className={classes.folderStructureIcon} />
                    {translations["view_as_table"] || "View as table"}
                </Fab>
            </div>
        </div>

    const handleToggle = (event, nodeIds) => {
        setExpanded(nodeIds);
    };

    const recursiveGetGroupIDs = (groups) => {
        if (!(groups instanceof Array)) {
            return [];
        }
        const ids = [];
        for (const group of groups) {
            ids.push(group.GroupID.toString());
            if (group.Children && group.Children.length>0) {
                ids.push(...recursiveGetGroupIDs(group.Children))
            }
        }
        return ids;
    }

    const expandAll = () => {
        setExpanded(recursiveGetGroupIDs(groups));
        setIsExpanded(true);
    }

    const collapseAll = () => {
        setExpanded([]);
        setIsExpanded(false);
    }

    const handleSelect = (event, nodeIds) => {
        setSelected(nodeIds);
    };

    const getTreeViewGroups = (groups) => {
        if (!(groups instanceof Array)) {
            return;
        }

        return groups?.map((group) => {return (
            <React.Fragment key={group.GroupID}>
                <TreeItem nodeId={group.GroupID.toString()} label={group.Name}>
                    {group.Children? getTreeViewGroups(Object.keys(group.Children).map((key) => group.Children[key])) : ""}
                </TreeItem>
            </React.Fragment>
        )})
    }

    return (
        <div className={classes.root}>
            {header}
            {groups && groups.length>0 ? <Button onClick={isExpanded? collapseAll : expandAll}>{isExpanded? translations["collapse_all"] || "Collapse all" : translations["expand_all"] || "Expand all"}</Button> :
            <Typography className={classes.typography}>{translations['no_data_to_show'] || 'No data to show'}</Typography>}
            <TreeView
                className={classes.treeView}
                defaultCollapseIcon={<ExpandMore />}
                defaultExpandIcon={<ChevronRight />}
                expanded={expanded}
                selected={selected}
                onNodeToggle={handleToggle}
                onNodeSelect={handleSelect}
            >
                {getTreeViewGroups(groups)}
            </TreeView>
            <Dialog disableBackdropClick={true} disableEscapeKeyDown={false} open={loading} PaperProps={{classes: {root: classes.loading}}}>
                <AppLogoLoading />
            </Dialog>
        </div>
    )
}

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        permissions: state?.core?.login?.permissions || [],
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
    };
};

const mapDispatchToProps = dispatch => {
    return {
        fetchAncestorGroups: id => dispatch(groupActions.fetchAncestorGroups(id)),
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(GroupFolderStructureView));